import React, { useState, useEffect } from 'react';
import { artistsAPI, uploadAPI } from '../../services/api';
import '../../App.css';

function ArtistsAdmin() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    image: '',
  });

  useEffect(() => {
    loadArtists();
  }, []);

  const loadArtists = async () => {
    try {
      const response = await artistsAPI.getAll();
      // Ensure we always have an array
      const data = response?.data || [];
      // Reverse to show newest first in admin
      const artistsArray = Array.isArray(data) ? data : [];
      setArtists([...artistsArray].reverse());
    } catch (error) {
      console.error('Error loading artists:', error);
      alert('Error loading artists');
      setArtists([]);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('File is too large. Maximum size is 10MB.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file.');
        e.target.value = ''; // Clear the input
        return;
      }
      
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      let imageUrl = formData.image;

      // Upload image if file is selected
      if (imageFile) {
        const uploadResponse = await uploadAPI.uploadImage(imageFile);
        imageUrl = uploadResponse.data.url;
      }

      const submitData = {
        ...formData,
        image: imageUrl,
      };

      if (editingId) {
        await artistsAPI.update(editingId, submitData);
      } else {
        await artistsAPI.create(submitData);
      }
      
      setFormData({ name: '', image: '' });
      setImageFile(null);
      setImagePreview('');
      setEditingId(null);
      loadArtists();
      alert('Artist saved!');
    } catch (error) {
      console.error('Error saving artist:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error saving artist';
      if (errorMessage.includes('too large') || errorMessage.includes('LIMIT_FILE_SIZE')) {
        alert('File is too large. Maximum size is 10MB.');
      } else {
        alert(errorMessage);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (artist) => {
    setFormData({
      name: artist.name || '',
      image: artist.image || '',
    });
    setImageFile(null);
    setImagePreview(artist.image || '');
    setEditingId(artist.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this artist?')) {
      try {
        await artistsAPI.delete(id);
        loadArtists();
      } catch (error) {
        console.error('Error deleting artist:', error);
        alert('Error deleting artist');
      }
    }
  };

  const handleMove = async (id, direction) => {
    try {
      await artistsAPI.move(id, direction);
      loadArtists();
    } catch (error) {
      console.error('Error moving artist:', error);
      alert(error.response?.data?.error || 'Error moving artist');
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', image: '' });
    setImageFile(null);
    setImagePreview('');
    setEditingId(null);
  };

  if (loading) {
    return <div className="empty-state">Loading artists...</div>;
  }

  return (
    <div>
      <h2 style={{ fontFamily: 'Bungee, cursive', marginBottom: '20px', color: '#228b22' }}>
        {editingId ? 'EDIT ARTIST' : 'ADD NEW ARTIST'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Artist Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Image (optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          {imagePreview && (
            <div style={{ marginTop: '10px' }}>
              <img 
                src={imagePreview} 
                alt="Preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
              />
            </div>
          )}
          {formData.image && !imageFile && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ fontSize: '0.9rem', color: '#a0a0a0' }}>Current: {formData.image}</p>
              <img 
                src={formData.image} 
                alt="Current" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', marginTop: '5px' }}
              />
            </div>
          )}
        </div>
        <button type="submit" className="btn" disabled={uploading}>
          {uploading ? 'UPLOADING...' : editingId ? 'UPDATE ARTIST' : 'ADD ARTIST'}
        </button>
        {editingId && (
          <button type="button" className="btn btn-secondary" onClick={handleCancel}>
            CANCEL
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={() => setFormData({ name: '', image: '' })}>
          CLEAR
        </button>
      </form>

      <div className="items-list">
        {!Array.isArray(artists) || artists.length === 0 ? (
          <div className="empty-state">No artists yet. Add one above!</div>
        ) : (
          artists.map((artist) => (
            <div key={artist.id} className="item-card">
              <h3>{artist.name}</h3>
              {artist.image && <p><strong>Image:</strong> {artist.image}</p>}
              <div className="item-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(artist.id, 'up')}
                  disabled={artists.indexOf(artist) === 0}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Up"
                >
                  ↑
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => handleMove(artist.id, 'down')}
                  disabled={artists.indexOf(artist) === artists.length - 1}
                  style={{ marginRight: '5px', minWidth: '40px' }}
                  title="Move Down"
                >
                  ↓
                </button>
                <button className="btn btn-secondary" onClick={() => handleEdit(artist)}>
                  EDIT
                </button>
                <button className="btn btn-danger" onClick={() => handleDelete(artist.id)}>
                  DELETE
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ArtistsAdmin;
